import { createSignal, Show, For, createEffect, createMemo } from 'solid-js'
import { userStore } from '/web/store'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { toastStore } from '/web/store'
import { getAvailableTokenOptions } from '/common/token-plans'
import { redirectToCheckout } from '/web/shared/aifans/checkout'
import './HentaiBuyTokens.css'

const BADGES: Record<number, { label: string; class: string } | undefined> = {
  100: {
    label: '⚡ Most Popular',
    class: 'text-white',
  },
  400: {
    label: '🏆 Best Value',
    class: 'text-[#1a0a0c]',
  },
}

const PERKS = ['Live 24/7 Chat', 'AI Image Generation', '1,000+ Characters', 'Exclusive Content']

const HentaiBuyTokensModal = () => {
  const state = userStore()
  const [selectedTokens, setSelectedTokens] = createSignal<number>(0)
  const [hasClaimedInitial, setHasClaimedInitial] = createSignal(
    state.profile?.hasClaimedInitialTokens ?? false
  )

  const options = createMemo(() => getAvailableTokenOptions(hasClaimedInitial()))
  const selectedOption = createMemo(() => options().find((o) => o.amount === selectedTokens()))

  const ctaLabel = createMemo(() => {
    const opt = selectedOption()
    if (!opt) return 'Continue to Checkout'
    if (opt.firstTimeOnly) return 'Claim My Free Tokens'
    return `Continue to Checkout — $${opt.price}`
  })

  const closeModal = () => {
    userStore.buyTokensModal(false)
  }

  // #225: redirect to Epoch's hosted payment page instead of embedding it in an iframe.
  const purchase = async () => {
    const option = selectedOption()
    if (!option) return

    try {
      const response = await aifansSubscriptionApi.createTokenReloadLink(
        option.price,
        option.id,
        option.amount
      )

      if (!redirectToCheckout(response.result?.redirectURL)) {
        toastStore.error('There was a problem processing your request. Please try again.')
      }
    } catch (error) {
      toastStore.error('There was a problem processing your request. Please try again.')
    }
  }

  createEffect(() => {
    const claimed = state.profile?.hasClaimedInitialTokens ?? false
    setHasClaimedInitial(claimed)
    setSelectedTokens(getAvailableTokenOptions(claimed)[0].amount)
  })

  // Only show on /hentai pages
  const isHentaiPage = () => {
    const path = window.location.pathname
    return path.startsWith('/hentai') || path.startsWith('/lp/hentai')
  }

  return (
    <Show when={state.showBuyTokens && isHentaiPage()}>
      <div class="fixed inset-0 z-[200] overflow-y-auto bg-black/90" onClick={closeModal}>
        <div
          class={`relative mx-auto my-4 w-[calc(100%-24px)] max-w-[640px] overflow-hidden rounded-[20px] md:my-8 ${
            hasClaimedInitial() ? 'hbt-shimmer' : ''
          }`}
          style={
            hasClaimedInitial()
              ? 'background: linear-gradient(160deg, #150a0c 0%, #0a0607 100%); border: 1px solid rgba(239,45,86,0.25); box-shadow: 0 0 80px rgba(239,45,86,0.15), 0 30px 80px rgba(0,0,0,0.7)'
              : 'background: #110a0b; border: 1.5px solid rgba(224, 48, 66, 0.5); box-shadow: 0 0 60px rgba(224, 48, 66, 0.15), 0 30px 80px rgba(0,0,0,0.6)'
          }
          onClick={(e) => e.stopPropagation()}
        >
          <Show when={!hasClaimedInitial()} fallback={<ReturningUserContent />}>
            <FreeTokenContent closeModal={closeModal} purchase={purchase} />
          </Show>
        </div>
      </div>
    </Show>
  )

  function FreeTokenContent(props: { closeModal: () => void; purchase: () => void }) {
    return (
      <>
        {/* Banner */}
        <div
          class="hbt-modal-banner px-5 pb-6 pt-7 md:px-9 md:pb-7 md:pt-8"
          style="background: linear-gradient(135deg, #1a0a0c 0%, #100a0b 50%, #120808 100%); border-bottom: 1px solid rgba(224,48,66,0.2)"
        >
          {/* Close button */}
          <button
            onClick={props.closeModal}
            class="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full text-base transition-all hover:rotate-90"
            style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); color: #888"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,60,172,0.18)'
              e.currentTarget.style.color = '#fff'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.06)'
              e.currentTarget.style.color = '#888'
            }}
          >
            &#10005;
          </button>

          <div
            class="hbt-banner-eyebrow flex items-center gap-2"
            style="font-family: 'Rajdhani', sans-serif; font-size: 11px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #FF3CAC; margin-bottom: 10px; position: relative; z-index: 1"
          >
            Limited Time Offer
          </div>

          <div
            class="text-[34px] md:text-[46px]"
            style="font-family: 'Bebas Neue', sans-serif; font-weight: 400; line-height: 0.95; text-transform: uppercase; letter-spacing: -0.01em; position: relative; z-index: 1; margin-bottom: 14px"
          >
            Get{' '}
            <span style="background: linear-gradient(90deg, #e03042, #ff4d5e); -webkit-background-clip: text; -webkit-text-fill-color: transparent">
              25 Tokens
            </span>
            <br />
            <span style="color: transparent; -webkit-text-stroke: 1.5px rgba(255,255,255,0.25)">
              Completely
            </span>{' '}
            Free
          </div>

          <div
            class="text-[13px] md:text-[14px]"
            style="color: rgba(255,255,255,0.55); line-height: 1.5; position: relative; z-index: 1; max-width: 380px"
          >
            Create your account and instantly unlock tokens to access live chats, exclusive content,
            and AI image generation — no charge, ever.
          </div>

          <div
            class="flex flex-wrap gap-3 md:gap-5"
            style="margin-top: 20px; position: relative; z-index: 1"
          >
            <For each={PERKS}>
              {(perk) => (
                <div
                  class="flex items-center gap-[7px]"
                  style="font-size: 12.5px; font-weight: 600; color: rgba(255,255,255,0.6)"
                >
                  <div
                    class="flex-shrink-0 rounded-full"
                    style="width: 6px; height: 6px; background: #00E5FF"
                  />
                  {perk}
                </div>
              )}
            </For>
          </div>
        </div>

        {/* Body */}
        <div class="px-5 py-6 md:px-9 md:py-7">
          {/* Big token card */}
          <div
            class="hbt-free-card relative mb-5 flex items-center gap-4 overflow-hidden rounded-xl md:gap-5"
            style="background: linear-gradient(135deg, rgba(224,48,66,0.12) 0%, rgba(224,48,66,0.04) 100%); border: 1.5px solid rgba(224,48,66,0.4); padding: 18px 16px; padding-top: 32px"
          >
            {/* Free badge */}
            <div
              class="absolute right-0 top-0 z-[2] text-white"
              style="background: linear-gradient(90deg, #e8192c, #ff4d5e); font-size: 9px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; padding: 4px 10px; border-radius: 0 12px 0 10px"
            >
              🎁 Free
            </div>

            {/* Radio */}
            <div class="relative flex-shrink-0">
              <div
                class="hbt-radio-pulse"
                style="inset: -6px; border-color: rgba(224,48,66,0.35)"
              />
              <div
                class="hbt-free-radio relative z-[1] flex items-center justify-center rounded-full"
                style="width: 24px; height: 24px; border: 2px solid #e03042"
              />
            </div>

            {/* Token info */}
            <div class="relative z-[1] flex-1">
              <div
                class="text-[48px] md:text-[64px]"
                style="font-family: 'Bebas Neue', sans-serif; font-weight: 400; line-height: 1; letter-spacing: -0.02em"
              >
                25
              </div>
              <div style="font-family: 'Rajdhani', sans-serif; font-size: 13px; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; color: #e03042; margin-top: 2px">
                Tokens
              </div>
            </div>

            {/* Price */}
            <div class="relative z-[1] text-right">
              <div style="font-size: 13px; color: #555; text-decoration: line-through; margin-bottom: 2px">
                $19.99
              </div>
              <div
                class="text-[28px] md:text-[42px]"
                style="font-family: 'Bebas Neue', sans-serif; font-weight: 400; line-height: 1; color: #e03042; letter-spacing: -0.01em"
              >
                FREE
              </div>
              <div style="font-family: 'Rajdhani', sans-serif; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #e03042; opacity: 0.75; margin-top: 3px">
                Today Only
              </div>
            </div>
          </div>

          {/* Payment row */}
          <PaymentRow />

          {/* CTA Button */}
          <button
            class="hbt-cta-btn flex w-full cursor-pointer items-center justify-center gap-[10px] rounded-[10px] border-none text-[15px] text-white md:text-[17px]"
            style="padding: 16px; background: linear-gradient(135deg, #8A2BE2, #FF3CAC); font-weight: 800; letter-spacing: 0.03em; box-shadow: 0 8px 30px rgba(138,43,226,0.35); transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s"
            onClick={props.purchase}
          >
            <span>Claim My Free Tokens</span>
            <span class="hbt-cta-arrow" style="font-size: 20px; transition: transform 0.2s">
              &rarr;
            </span>
          </button>

          {/* Fine print */}
          <div
            class="flex flex-wrap items-center justify-center gap-[14px]"
            style="text-align: center; font-size: 12px; color: rgba(255,255,255,0.35); margin-top: 14px"
          >
            <span class="flex items-center gap-[5px]">&#x1F512; Secure Checkout</span>
            <span class="flex items-center gap-[5px]">&#x2726; No Charge Today</span>
            <span class="flex items-center gap-[5px]">&#x2726; Cancel Anytime</span>
          </div>
        </div>
      </>
    )
  }

  function ReturningUserContent() {
    return (
      <>
        {/* Banner */}
        <div
          class="hbt-modal-banner px-5 pb-6 pt-7 md:px-9 md:pb-7 md:pt-8"
          style="background: linear-gradient(135deg, #1a0a0f 0%, #0d0608 50%, #1a0a0d 100%); border-bottom: 1px solid rgba(239,45,86,0.2)"
        >
          {/* Close button */}
          <button
            onClick={closeModal}
            class="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.06)] text-xl text-[#8a7377] transition-all hover:rotate-90 hover:bg-[rgba(255,60,172,0.18)] hover:text-white"
          >
            &#10005;
          </button>

          {/* Logo */}
          <div class="relative z-[1] mb-4">
            <img
              src="/images/cosplayfans-logo.png"
              alt="CosplayFans"
              class="block w-[55%] max-w-[180px]"
              style="filter: drop-shadow(0 4px 20px rgba(239,45,86,0.45))"
            />
          </div>

          <div
            class="hbt-banner-eyebrow flex items-center gap-2"
            style="font-family: 'Rajdhani', sans-serif; font-size: 11px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #FF3CAC; margin-bottom: 10px; position: relative; z-index: 1"
          >
            Power Your Experience
          </div>

          <div
            class="text-[34px] md:text-[44px]"
            style="font-family: 'Bebas Neue', sans-serif; font-weight: 400; line-height: 1.0; letter-spacing: -0.02em; text-transform: uppercase; position: relative; z-index: 1; margin-bottom: 12px"
          >
            Unlock{' '}
            <span style="background: linear-gradient(135deg, #ff4d70 0%, #ef2d56 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent">
              Premium
            </span>
            <br />
            <span style="color: transparent; -webkit-text-stroke: 1.5px rgba(255,255,255,0.28)">
              With
            </span>{' '}
            Tokens
          </div>

          <div
            class="text-[13px] md:text-[14px]"
            style="color: rgba(255,255,255,0.55); line-height: 1.55; position: relative; z-index: 1; max-width: 440px"
          >
            Tokens fuel everything — live chats, exclusive content, and AI image generation. Choose
            a package and start exploring.
          </div>

          <div
            class="flex flex-wrap gap-[18px]"
            style="margin-top: 18px; position: relative; z-index: 1"
          >
            <For each={PERKS}>
              {(perk) => (
                <div
                  class="flex items-center gap-[7px]"
                  style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.6)"
                >
                  <div
                    class="flex-shrink-0 rounded-full"
                    style="width: 6px; height: 6px; background: #00E5FF; box-shadow: 0 0 8px rgba(0,229,255,0.6)"
                  />
                  {perk}
                </div>
              )}
            </For>
          </div>
        </div>

        {/* Body */}
        <div class="px-5 py-6 md:px-9 md:py-7">
          <div style="font-family: 'Rajdhani', sans-serif; font-size: 11px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: #8a7377; margin-bottom: 12px">
            Select Your Token Package
          </div>

          {/* Token options */}
          <div class="flex flex-col gap-[10px]" style="margin-bottom: 18px">
            <For each={options()}>
              {(option) => {
                if (option.firstTimeOnly) return null
                const isSelected = () => selectedTokens() === option.amount
                const badge = BADGES[option.amount]
                return (
                  <div
                    class="hbt-token-card flex cursor-pointer items-center gap-3 overflow-hidden transition-all duration-200 md:gap-4"
                    classList={{ selected: isSelected() }}
                    style={{
                      background: isSelected()
                        ? 'linear-gradient(135deg, rgba(239,45,86,0.14) 0%, rgba(239,45,86,0.05) 100%)'
                        : 'rgba(255,255,255,0.03)',
                      border: isSelected()
                        ? '1.5px solid rgba(239,45,86,0.6)'
                        : '1.5px solid rgba(255,255,255,0.08)',
                      'border-radius': '12px',
                      padding: '18px 14px',
                      position: 'relative',
                      'box-shadow': isSelected() ? '0 0 0 3px rgba(239,45,86,0.1)' : 'none',
                    }}
                    onClick={() => setSelectedTokens(option.amount)}
                    onMouseEnter={(e) => {
                      if (!isSelected()) {
                        e.currentTarget.style.borderColor = 'rgba(239,45,86,0.35)'
                        e.currentTarget.style.background = 'rgba(239,45,86,0.04)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected()) {
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'
                        e.currentTarget.style.background = 'rgba(255,255,255,0.03)'
                      }
                    }}
                  >
                    {/* Badge */}
                    <Show when={badge}>
                      <div
                        class={`absolute right-0 top-0 z-[2] ${badge!.class}`}
                        style={{
                          'font-size': '9px',
                          'font-weight': '800',
                          'letter-spacing': '0.08em',
                          'text-transform': 'uppercase',
                          padding: '3px 8px',
                          'border-radius': '0 12px 0 8px',
                          background:
                            option.amount === 100
                              ? 'linear-gradient(90deg, #ff4d70 0%, #ef2d56 55%, #c71f3e 100%)'
                              : 'linear-gradient(90deg, #ffb347, #ff8c42)',
                          'box-shadow':
                            option.amount === 100 ? '0 2px 12px rgba(239,45,86,0.35)' : 'none',
                        }}
                      >
                        {badge!.label}
                      </div>
                    </Show>

                    {/* Radio */}
                    <div class="relative flex-shrink-0">
                      <Show when={isSelected()}>
                        <div class="hbt-radio-pulse" />
                      </Show>
                      <div
                        class="hbt-radio-dot relative z-[1] flex items-center justify-center rounded-full"
                        classList={{ active: isSelected() }}
                        style={{
                          width: '22px',
                          height: '22px',
                          border: `2px solid ${isSelected() ? '#ef2d56' : '#4a3236'}`,
                          transition: 'border-color 0.2s',
                        }}
                      />
                    </div>

                    {/* Token info */}
                    <div class="relative z-[1] flex flex-1 items-baseline gap-2">
                      <div
                        class="text-[30px] md:text-[36px]"
                        style="font-family: 'Bebas Neue', sans-serif; font-weight: 400; line-height: 1; letter-spacing: -0.02em"
                      >
                        {option.amount}
                      </div>
                      <div
                        style={{
                          'font-family': "'Rajdhani', sans-serif",
                          'font-size': '12px',
                          'font-weight': '700',
                          'letter-spacing': '0.14em',
                          'text-transform': 'uppercase',
                          color: isSelected() ? '#ff4d70' : '#6b5155',
                        }}
                      >
                        Tokens
                      </div>
                    </div>

                    {/* Price */}
                    <div class="relative z-[1] ml-auto flex flex-col items-end gap-[2px]">
                      <div
                        class="text-[22px] md:text-[26px]"
                        style={{
                          'font-family': "'Bebas Neue', sans-serif",
                          'font-weight': '400',
                          'line-height': '1',
                          'letter-spacing': '-0.01em',
                          color: isSelected() ? '#fff' : '#d4b7bb',
                        }}
                      >
                        ${option.price}
                      </div>
                    </div>
                  </div>
                )
              }}
            </For>
          </div>

          {/* Divider */}
          <div style="height: 1px; background: rgba(239,45,86,0.12); margin: 18px 0" />

          {/* Payment row */}
          <PaymentRow />

          {/* CTA Button (pill style) */}
          <button
            class="hbt-cta-btn hbt-cta-btn-pill flex w-full cursor-pointer items-center justify-center gap-[10px] rounded-full border-none text-[14px] uppercase text-white md:text-[15px]"
            style="padding: 17px; background: linear-gradient(135deg, #8A2BE2, #FF3CAC); font-weight: 800; letter-spacing: 0.06em; box-shadow: 0 8px 30px rgba(138,43,226,0.35), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.25s"
            onClick={purchase}
          >
            <span class="relative z-[1]">{ctaLabel()}</span>
            <span
              class="hbt-cta-arrow relative z-[1]"
              style="font-size: 18px; transition: transform 0.2s"
            >
              &rarr;
            </span>
          </button>

          {/* Fine print */}
          <div
            class="flex flex-wrap items-center justify-center gap-[14px]"
            style="text-align: center; font-size: 12px; color: rgba(255,255,255,0.35); margin-top: 14px"
          >
            <span class="flex items-center gap-[5px]">&#x1F512; Secure Checkout</span>
            <span class="flex items-center gap-[5px]">&#x2726; Instant Delivery</span>
            <span class="flex items-center gap-[5px]">&#x2726; Cancel Anytime</span>
          </div>
        </div>
      </>
    )
  }
}

function PaymentRow() {
  return (
    <div
      class="flex items-center gap-3"
      style="background: rgba(255,255,255,0.03); border: 1px solid rgba(239,45,86,0.22); border-radius: 12px; padding: 12px 14px; margin-bottom: 20px"
    >
      <div
        class="hbt-payment-radio flex flex-shrink-0 items-center justify-center rounded-full"
        style="width: 20px; height: 20px; border: 2px solid #ef2d56"
      />
      <div style="font-family: 'Rajdhani', sans-serif; font-size: 14px; font-weight: 600; color: #c9b4b7">
        Credit / Debit Card
      </div>
      <div class="ml-auto flex items-center gap-[6px]">
        <img src="/images/footer/visa-card.svg" alt="Visa" class="h-[18px] w-auto md:h-[22px]" />
        <img
          src="/images/footer/mastercard-card.svg"
          alt="Mastercard"
          class="h-[18px] w-auto md:h-[22px]"
        />
        <img
          src="/images/footer/discover-card.svg"
          alt="Discover"
          class="h-[18px] w-auto md:h-[22px]"
        />
      </div>
    </div>
  )
}

export default HentaiBuyTokensModal
